import { createContext, useCallback, useContext, useState } from 'react'
import Table from 'components/table/Table'
import { noop } from 'utils'
import PropTypes from 'prop-types'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'
import { shallowEqual } from 'react-redux'

export const MultiTableContext = createContext({
  selectedIds: [],
  onSelectionChange: noop,
  hoveredId: null,
  setHoveredId: noop,
  lockSelection: false,
})

const Container = ({
  selectedIds = [],
  onSelectionChange = noop,
  onHoverChange = noop,
  lockSelection = false,
  children,
}) => {
  const [hoveredId, setHoveredId] = useState(null)
  const onSelectionChangeRef = useUpdatingRef(onSelectionChange)

  const handleSelectionChange = useCallback(
    _selectedIds => {
      if (!shallowEqual(selectedIds, _selectedIds)) {
        onSelectionChangeRef.current(_selectedIds)
      }
    },
    [onSelectionChangeRef, selectedIds]
  )

  return (
    <MultiTableContext.Provider
      value={{
        selectedIds,
        onSelectionChange: handleSelectionChange,
        hoveredId,
        lockSelection,
        setHoveredId: newHoveredId => {
          onHoverChange(newHoveredId)
          setHoveredId(newHoveredId)
        },
      }}>
      {children}
    </MultiTableContext.Provider>
  )
}

Container.propTypes = {
  children: PropTypes.node,
  onHoverChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  lockSelection: PropTypes.bool,
  selectedIds: PropTypes.array,
}

const MultiTable = ({
  className,
  caption,
  error,
  loading,
  emptyMessage,
  loadingMessage,
  errorMessage,
  style,
  children,
}) => {
  const {
    selectedIds,
    onSelectionChange,
    hoveredId,
    setHoveredId,
    lockSelection,
  } = useContext(MultiTableContext)

  return (
    <>
      <Table
        mergeHeadersOnNoContent
        className={className}
        caption={caption}
        error={error}
        loading={loading}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        errorMessage={errorMessage}
        onSelectionChange={onSelectionChange}
        selectedIds={selectedIds}
        onHoverChange={setHoveredId}
        hoveredId={hoveredId}
        lockSelection={lockSelection}
        style={style}>
        {children}
      </Table>
    </>
  )
}

MultiTable.Container = Container
MultiTable.Container.displayName = 'MultiTable.Container'
MultiTable.propTypes = {
  caption: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  emptyMessage: PropTypes.node,
  error: PropTypes.bool,
  errorMessage: PropTypes.node,
  loading: PropTypes.bool,
  loadingMessage: PropTypes.node,
  style: PropTypes.object,
}
export default MultiTable
